import { useMemo, useState } from "react";
import {
  allocateReceipt, buildAgeing, buildLedger, buildPrintModel, buildStatement, computeDocumentTax,
  defaultSalesConfig, openInvoices, renderInvoiceHtml, validateDocument, hasBlockingIssues,
  allowedConversions, activeStages, DOC_LABELS as SALES_LABELS,
  type Receipt, type SalesConfig, type SalesDocument, type SupplierDetails
} from "./sales";
import {
  allocatePayment, buildPayablesAgeing, buildVendorLedger, defaultPurchaseConfig, openBills,
  assessItc, assessTds, threeWayMatch, validatePurchaseDocument, ITC_LABELS,
  DOC_LABELS as PURCHASE_LABELS,
  type Payment, type PurchaseConfig, type PurchaseDocument
} from "./purchase";
import type { Partner } from "./partner";

/** Matches the notification tones the host app uses. */
export type Tone = "blue" | "green" | "orange" | "red" | "slate" | "purple" | "neutral";

const inr = (value: number) =>
  `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(Math.round(value * 100) / 100)}`;

const shortDate = (value?: string) => {
  if (!value) return "—";
  const [y, m, d] = value.slice(0, 10).split("-");
  return `${d}/${m}/${y.slice(2)}`;
};

const statusTone = (status: string): string => {
  if (["Posted", "Paid", "Delivered", "Received", "Confirmed", "Accepted"].includes(status)) return "green";
  if (["Pending review", "Partially paid", "Partially delivered", "Partially received", "On hold"].includes(status)) return "orange";
  if (["Cancelled", "Rejected"].includes(status)) return "red";
  if (status === "Approved") return "blue";
  return "slate";
};

function Pill({ children, tone = "slate" }: { children: React.ReactNode; tone?: string }) {
  return <span className={`ws-pill ws-${tone}`}>{children}</span>;
}

/** The configured document chain, showing which stages are required. */
function StageChain({ config }: { config: SalesConfig }) {
  const stages = activeStages(config);
  const optional: Array<{ key: string; label: string }> = [];
  if (!config.requireQuotation) optional.push({ key: "quotation", label: "Quotation" });
  if (!config.requireSalesOrder) optional.push({ key: "sales-order", label: "Sales order" });
  if (!config.requireDeliveryNote) optional.push({ key: "delivery-note", label: "Delivery note" });

  return (
    <div className="ws-chain">
      <div className="ws-chain-row">
        {stages.map((stage, index) => (
          <span key={stage} className="ws-chain-item">
            <b>{SALES_LABELS[stage]}</b>
            {index < stages.length - 1 && <i>→</i>}
          </span>
        ))}
      </div>
      {optional.length > 0 && (
        <p className="ws-muted">
          Optional in this configuration: {optional.map((entry) => entry.label).join(", ")}. Turn a stage on in Configuration to make it mandatory before invoicing.
        </p>
      )}
    </div>
  );
}

interface SalesProps {
  onPreviewDocument?:(id:string)=>void;
  currency?:string;
  documents: SalesDocument[];
  receipts: Receipt[];
  partners: Partner[];
  config?: SalesConfig;
  supplier: SupplierDetails;
  onOpenPreview?: (html: string) => void;
  notify?: (message: string, tone?: Tone) => void;
  initialTab?: "documents" | "ledger" | "ageing" | "receipts";
}

export function SalesWorkspace({ onPreviewDocument,currency='INR',documents, receipts, partners, config = defaultSalesConfig("29"), supplier, onOpenPreview, notify, initialTab }: SalesProps) {
  const [tab, setTab] = useState<"documents" | "ledger" | "ageing" | "receipts">(initialTab ?? "documents");
  const [selectedPartner, setSelectedPartner] = useState<string>(partners[0]?.id ?? "");
  const [selectedDoc, setSelectedDoc] = useState<string | null>(null);
  const inr=(value:number)=>new Intl.NumberFormat("en-GB",{style:"currency",currency}).format(value);
  const asAt = new Date().toISOString().slice(0, 10);

  const partnerList = useMemo(() => partners.map((partner) => ({ id: partner.id, name: partner.name })), [partners]);

  const ageing = useMemo(
    () => buildAgeing(partnerList, documents, receipts, config, { asAt }),
    [partnerList, documents, receipts, config, asAt]
  );

  const ledger = useMemo(
    () => (selectedPartner ? buildLedger(selectedPartner, documents, receipts, config, {}) : null),
    [selectedPartner, documents, receipts, config]
  );

  const statement = useMemo(
    () => (selectedPartner
      ? buildStatement(
          { id: selectedPartner, name: partnerList.find((p) => p.id === selectedPartner)?.name ?? "" },
          documents, receipts, config,
          { from: `${new Date(asAt).getFullYear()}-04-01`, to: asAt }
        )
      : null),
    [selectedPartner, documents, receipts, config, asAt, partnerList]
  );

  const active = documents.find((document) => document.id === selectedDoc);

  const preview = (document: SalesDocument) => {
    if(onPreviewDocument){onPreviewDocument(document.id);return;}
    const partner = partners.find((entry) => entry.id === document.partnerId);
    const model = buildPrintModel(document, supplier, {
      name: document.partnerName,
      gstin: document.partnerGstin,
      placeOfSupply: document.placeOfSupply,
      billingAddress: [partner?.billingAddress?.line1, partner?.billingAddress?.city, partner?.billingAddress?.pincode].filter(Boolean).join(", "),
      shippingAddress: [partner?.shippingAddress?.line1, partner?.shippingAddress?.city].filter(Boolean).join(", ")
    }, config);

    if (model.missingParticulars.length) {
      notify?.(`Not ready to issue — rule 46 requires: ${model.missingParticulars.join(", ")}`, "orange");
    }
    const html = renderInvoiceHtml(model);
    if (onOpenPreview) onOpenPreview(html);
    else {
      const window_ = window.open("", "_blank");
      window_?.document.write(html);
      window_?.document.close();
    }
  };

  return (
    <div className="ws">
      <StageChain config={config} />

      <div className="ws-tabs">
        {(["documents", "receipts", "ledger", "ageing"] as const).map((key) => (
          <button key={key} className={tab === key ? "active" : ""} onClick={() => setTab(key)} type="button">
            {key === "documents" ? "Documents" : key === "receipts" ? "Receipts" : key === "ledger" ? "Customer ledger" : "Ageing"}
          </button>
        ))}
      </div>

      {tab === "documents" && (
        <div className="ws-panel">
          <table className="ws-table">
            <thead>
              <tr><th>Type</th><th>Number</th><th>Date</th><th>Customer</th><th className="num">Taxable</th><th className="num">Tax</th><th className="num">Total</th><th>Status</th><th>Next</th><th /></tr>
            </thead>
            <tbody>
              {documents.map((document) => {
                const tax = computeDocumentTax(document.lines, document.charges, document.documentDiscount, {canonical:document.canonical,adjustments:document.adjustments,
                  supplyType: document.supplyType, rcm: document.rcm, roundOff: config.roundOff, tcsRate: document.tcsRate
                });
                const next = allowedConversions(document, config);
                const issues = validateDocument(document, config, { outstandingBalance: 0 });
                return (
                  <tr key={document.id} className={selectedDoc === document.id ? "selected" : ""} onClick={() => setSelectedDoc(document.id)}>
                    <td>{SALES_LABELS[document.type]}</td>
                    <td><strong>{document.number}</strong>{document.rcm && <Pill tone="orange">RCM</Pill>}</td>
                    <td>{shortDate(document.date)}</td>
                    <td>{document.partnerName}<span className="ws-sub">{document.partnerGstin ?? "Unregistered"} · {document.supplyType}</span></td>
                    <td className="num">{inr(tax.taxableValue)}</td>
                    <td className="num">{inr(tax.totalTax)}</td>
                    <td className="num"><strong>{inr(tax.total)}</strong></td>
                    <td><Pill tone={statusTone(document.status)}>{document.status}</Pill>{hasBlockingIssues(issues) && <Pill tone="red">{issues.filter((i) => i.severity === "error").length} issue</Pill>}</td>
                    <td>{next.length ? next.map((entry) => SALES_LABELS[entry]).join(", ") : "—"}</td>
                    <td><button className="ws-link" type="button" onClick={(event) => { event.stopPropagation(); preview(document); }}>Preview</button></td>
                  </tr>
                );
              })}
              {documents.length === 0 && <tr><td colSpan={10} className="ws-empty">No sales documents yet.</td></tr>}
            </tbody>
          </table>

          {active && (
            <div className="ws-detail">
              <h4>{SALES_LABELS[active.type]} {active.number}</h4>
              <ValidationList issues={validateDocument(active, config, { outstandingBalance: 0 })} />
              <table className="ws-table compact">
                <thead><tr><th>Description</th><th>HSN</th><th className="num">Qty</th><th className="num">Rate</th><th className="num">Taxable</th><th className="num">GST</th><th className="num">Total</th></tr></thead>
                <tbody>
                  {computeDocumentTax(active.lines, active.charges, active.documentDiscount, {canonical:active.canonical,adjustments:active.adjustments, supplyType: active.supplyType, rcm: active.rcm, roundOff: config.roundOff }).lines.map((computed, index) => (
                    <tr key={computed.lineId}>
                      <td>{active.lines[index]?.description}</td>
                      <td className="mono">{active.lines[index]?.hsnSac ?? "—"}</td>
                      <td className="num">{active.lines[index]?.quantity}</td>
                      <td className="num">{inr(active.lines[index]?.unitPrice ?? 0)}</td>
                      <td className="num">{inr(computed.taxableValue)}</td>
                      <td className="num">{inr(computed.totalTax)} <span className="ws-sub">{computed.gstRate}%</span></td>
                      <td className="num"><strong>{inr(computed.lineTotal)}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === "receipts" && (
        <div className="ws-panel">
          <table className="ws-table">
            <thead><tr><th>Receipt</th><th>Date</th><th>Customer</th><th>Mode</th><th className="num">Amount</th><th className="num">TDS</th><th>Allocated to</th><th className="num">Unallocated</th></tr></thead>
            <tbody>
              {receipts.map((entry) => (
                <tr key={entry.id}>
                  <td><strong>{entry.number}</strong></td>
                  <td>{shortDate(entry.date)}</td>
                  <td>{entry.partnerName}</td>
                  <td>{entry.mode.replace("-", " ")}</td>
                  <td className="num">{inr(entry.amount)}</td>
                  <td className="num">{entry.tdsDeducted ? inr(entry.tdsDeducted) : "—"}</td>
                  <td>{entry.allocations.map((allocation) => allocation.invoiceNumber).join(", ") || <span className="ws-muted">Unapplied</span>}</td>
                  <td className="num">{entry.unallocated ? <Pill tone="orange">{inr(entry.unallocated)}</Pill> : "—"}</td>
                </tr>
              ))}
              {receipts.length === 0 && <tr><td colSpan={8} className="ws-empty">No receipts recorded.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "ledger" && (
        <div className="ws-panel">
          <div className="ws-toolbar">
            <select value={selectedPartner} onChange={(event) => setSelectedPartner(event.target.value)}>
              {partnerList.map((partner) => <option key={partner.id} value={partner.id}>{partner.name}</option>)}
            </select>
            {statement && (
              <div className="ws-stats">
                <span>Opening <strong>{inr(statement.openingBalance)}</strong></span>
                <span>Debit <strong>{inr(statement.totalDebit)}</strong></span>
                <span>Credit <strong>{inr(statement.totalCredit)}</strong></span>
                <span>Closing <strong>{inr(statement.closingBalance)}</strong></span>
                <span>Overdue <strong className="ws-red">{inr(statement.overdueAmount)}</strong></span>
              </div>
            )}
          </div>
          <table className="ws-table">
            <thead><tr><th>Date</th><th>Reference</th><th>Particulars</th><th className="num">Debit</th><th className="num">Credit</th><th className="num">Balance</th></tr></thead>
            <tbody>
              {ledger?.entries.map((entry) => (
                <tr key={entry.id}>
                  <td>{shortDate(entry.date)}</td>
                  <td><strong>{entry.reference}</strong></td>
                  <td>{entry.narration}</td>
                  <td className="num">{entry.debit ? inr(entry.debit) : "—"}</td>
                  <td className="num">{entry.credit ? inr(entry.credit) : "—"}</td>
                  <td className="num"><strong>{inr(entry.runningBalance)}</strong></td>
                </tr>
              ))}
              {!ledger?.entries.length && <tr><td colSpan={6} className="ws-empty">No transactions for this customer.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "ageing" && (
        <div className="ws-panel">
          <table className="ws-table">
            <thead>
              <tr><th>Customer</th>{ageing.bucketLabels.map((label) => <th key={label} className="num">{label}</th>)}<th className="num">Total</th><th className="num">Oldest</th></tr>
            </thead>
            <tbody>
              {ageing.rows.map((row) => (
                <tr key={row.partnerId}>
                  <td><strong>{row.partnerName}</strong></td>
                  {ageing.bucketLabels.map((label) => <td key={label} className="num">{row.buckets[label] ? inr(row.buckets[label]) : "—"}</td>)}
                  <td className="num"><strong>{inr(row.total)}</strong></td>
                  <td className="num">{row.oldestDays}d</td>
                </tr>
              ))}
              {ageing.rows.length === 0 && <tr><td colSpan={ageing.bucketLabels.length + 3} className="ws-empty">Nothing outstanding.</td></tr>}
            </tbody>
            {ageing.rows.length > 0 && (
              <tfoot>
                <tr><th>Total</th>{ageing.bucketLabels.map((label) => <th key={label} className="num">{inr(ageing.totals[label])}</th>)}<th className="num">{inr(ageing.grandTotal)}</th><th /></tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
}

function ValidationList({ issues }: { issues: Array<{ severity: string; message: string }> }) {
  if (!issues.length) return <div className="ws-ok">All checks passed.</div>;
  return (
    <ul className="ws-issues">
      {issues.map((issue, index) => (
        <li key={index} className={issue.severity === "error" ? "ws-issue-error" : "ws-issue-warn"}>
          <b>{issue.severity === "error" ? "Blocks posting" : "Review"}</b> {issue.message}
        </li>
      ))}
    </ul>
  );
}

interface PurchaseProps {
  onPreviewDocument?:(id:string)=>void;
  currency?:string;
  documents: PurchaseDocument[];
  payments: Payment[];
  partners: Partner[];
  config?: PurchaseConfig;
  initialTab?: "documents" | "itc" | "ledger" | "ageing";
}

export function PurchaseWorkspace({ onPreviewDocument,currency='INR',documents, payments, partners, config = defaultPurchaseConfig("29"), initialTab }: PurchaseProps) {
  const [tab, setTab] = useState<"documents" | "itc" | "ledger" | "ageing">(initialTab ?? "documents");
  const [selectedPartner, setSelectedPartner] = useState<string>(partners[0]?.id ?? "");
  const [selectedDoc, setSelectedDoc] = useState<string | null>(null);
  const inr=(value:number)=>new Intl.NumberFormat("en-GB",{style:"currency",currency}).format(value);
  const asAt = new Date().toISOString().slice(0, 10);

  const ageing = useMemo(
    () => buildPayablesAgeing(partners, documents, payments, config, { asAt }),
    [partners, documents, payments, config, asAt]
  );

  const ledger = useMemo(
    () => (selectedPartner ? buildVendorLedger(selectedPartner, documents, payments, config, {}) : null),
    [selectedPartner, documents, payments, config]
  );

  const active = documents.find((document) => document.id === selectedDoc);
  const activePartner = partners.find((partner) => partner.id === active?.partnerId);

  return (
    <div className="ws">
      <div className="ws-chain">
        <div className="ws-chain-row">
          {[
            config.requirePurchaseOrder && "purchase-order",
            config.requireGoodsReceipt && "goods-receipt",
            "bill"
          ].filter(Boolean).map((stage, index, all) => (
            <span key={String(stage)} className="ws-chain-item">
              <b>{PURCHASE_LABELS[stage as keyof typeof PURCHASE_LABELS]}</b>
              {index < all.length - 1 && <i>→</i>}
            </span>
          ))}
        </div>
        <p className="ws-muted">
          {config.enforceThreeWayMatch
            ? `Three-way match enforced with ${config.quantityTolerancePercent}% quantity and ${config.priceTolerancePercent}% price tolerance.`
            : "Three-way match is off. Bills post without checking against the order and receipt."}
        </p>
      </div>

      <div className="ws-tabs">
        {(["documents", "itc", "ledger", "ageing"] as const).map((key) => (
          <button key={key} className={tab === key ? "active" : ""} onClick={() => setTab(key)} type="button">
            {key === "documents" ? "Documents" : key === "itc" ? "Input credit" : key === "ledger" ? "Vendor ledger" : "Payables & MSME"}
          </button>
        ))}
      </div>

      {tab === "documents" && (
        <div className="ws-panel">
          <table className="ws-table">
            <thead><tr><th>Type</th><th>Number</th><th>Vendor ref</th><th>Date</th><th>Vendor</th><th className="num">Total</th><th>TDS</th><th>2B</th><th>Status</th></tr></thead>
            <tbody>
              {documents.map((document) => {
                const tax = computeDocumentTax(document.lines, document.charges, document.documentDiscount, {canonical:document.canonical,adjustments:document.adjustments,
                  supplyType: document.supplyType, rcm: document.rcm, roundOff: config.roundOff
                });
                return (
                  <tr key={document.id} className={selectedDoc === document.id ? "selected" : ""} onClick={() => setSelectedDoc(document.id)}>
                    <td>{PURCHASE_LABELS[document.type]}</td>
                    <td><strong>{document.number}</strong>{document.rcm && <Pill tone="orange">RCM</Pill>}</td>
                    <td className="mono">{document.vendorDocumentNumber ?? <Pill tone="red">Missing</Pill>}</td>
                    <td>{shortDate(document.date)}</td>
                    <td>{document.partnerName}</td>
                    <td className="num"><strong>{inr(tax.total)}</strong></td>
                    <td>{document.tdsSection ? <Pill tone="blue">{document.tdsSection} · {document.tdsRate??0}% selected</Pill> : "—"}</td>
                    <td>{document.reflectedIn2B === undefined ? "—" : document.reflectedIn2B ? <Pill tone="green">Yes</Pill> : <Pill tone="orange">No</Pill>}</td>
                    <td><Pill tone={statusTone(document.status)}>{document.status}</Pill></td>
                  </tr>
                );
              })}
              {documents.length === 0 && <tr><td colSpan={9} className="ws-empty">No purchase documents yet.</td></tr>}
            </tbody>
          </table>

          {active && (
            <div className="ws-detail">
              <h4>{PURCHASE_LABELS[active.type]} {active.number}</h4>{onPreviewDocument&&<button type="button" className="ws-link" onClick={()=>onPreviewDocument(active.id)}>Preview / print bill</button>}
              <ValidationList issues={validatePurchaseDocument(active, config, { partner: activePartner })} />
              {config.enforceThreeWayMatch && active.type === "bill" && (
                <MatchTable bill={active} documents={documents} config={config} />
              )}
            </div>
          )}
        </div>
      )}

      {tab === "itc" && (
        <div className="ws-panel">
          <table className="ws-table">
            <thead><tr><th>Bill</th><th>Vendor</th><th className="num">Tax</th><th className="num">Claimable</th><th className="num">Blocked</th><th className="num">Deferred</th><th>Status</th><th>Reason</th></tr></thead>
            <tbody>
              {documents.filter((document) => document.type === "bill").map((document) => {
                const tax = computeDocumentTax(document.lines, document.charges, document.documentDiscount, {canonical:document.canonical,adjustments:document.adjustments,
                  supplyType: document.supplyType, rcm: document.rcm, roundOff: config.roundOff
                });
                const itc = assessItc(document, tax.totalTax, {
                  reflectedIn2B: document.reflectedIn2B, received: true, asAt,
                  blockWithout2B: config.blockItcWithout2B
                });
                return (
                  <tr key={document.id}>
                    <td><strong>{document.number}</strong><span className="ws-sub">{document.vendorDocumentNumber}</span></td>
                    <td>{document.partnerName}</td>
                    <td className="num">{inr(tax.totalTax)}</td>
                    <td className="num ws-green">{inr(itc.claimable)}</td>
                    <td className="num ws-red">{itc.blocked ? inr(itc.blocked) : "—"}</td>
                    <td className="num ws-orange">{itc.deferred ? inr(itc.deferred) : "—"}</td>
                    <td><Pill tone={itc.eligibility === "eligible" ? "green" : itc.eligibility === "pending-2b" ? "orange" : "red"}>{ITC_LABELS[itc.eligibility]}</Pill></td>
                    <td className="ws-sub">{itc.reasons[0]}{itc.clauses.length ? ` (${itc.clauses.join(", ")})` : ""}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {tab === "ledger" && (
        <div className="ws-panel">
          <div className="ws-toolbar">
            <select value={selectedPartner} onChange={(event) => setSelectedPartner(event.target.value)}>
              {partners.map((partner) => <option key={partner.id} value={partner.id}>{partner.name}</option>)}
            </select>
            {ledger && (
              <div className="ws-stats">
                <span>Debit <strong>{inr(ledger.totalDebit)}</strong></span>
                <span>Credit <strong>{inr(ledger.totalCredit)}</strong></span>
                <span>Payable <strong>{inr(ledger.closingBalance)}</strong></span>
              </div>
            )}
          </div>
          <table className="ws-table">
            <thead><tr><th>Date</th><th>Reference</th><th>Vendor ref</th><th>Particulars</th><th className="num">Debit</th><th className="num">Credit</th><th className="num">Balance</th></tr></thead>
            <tbody>
              {ledger?.entries.map((entry) => (
                <tr key={entry.id}>
                  <td>{shortDate(entry.date)}</td>
                  <td><strong>{entry.reference}</strong></td>
                  <td className="mono">{entry.vendorReference ?? "—"}</td>
                  <td>{entry.narration}</td>
                  <td className="num">{entry.debit ? inr(entry.debit) : "—"}</td>
                  <td className="num">{entry.credit ? inr(entry.credit) : "—"}</td>
                  <td className="num"><strong>{inr(entry.runningBalance)}</strong></td>
                </tr>
              ))}
              {!ledger?.entries.length && <tr><td colSpan={7} className="ws-empty">No transactions for this vendor.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "ageing" && (
        <div className="ws-panel">
          <div className="ws-stats spread">
            <span>Total payable <strong>{inr(ageing.grandTotal)}</strong></span>
            <span>To MSME vendors <strong>{inr(ageing.msmeTotal)}</strong></span>
            <span>Disallowable u/s 43B(h) <strong className="ws-red">{inr(ageing.disallowableTotal)}</strong></span>
          </div>
          {ageing.disallowableTotal > 0 && (
            <div className="ws-alert">
              {inr(ageing.disallowableTotal)} owed to micro and small vendors is past the agreed period. Under section 43B(h) this is disallowed for income tax until it is actually paid.
            </div>
          )}
          <table className="ws-table">
            <thead>
              <tr><th>Vendor</th><th>MSME</th>{ageing.bucketLabels.map((label) => <th key={label} className="num">{label}</th>)}<th className="num">Total</th><th className="num">43B(h)</th></tr>
            </thead>
            <tbody>
              {ageing.rows.map((row) => (
                <tr key={row.partnerId}>
                  <td><strong>{row.partnerName}</strong></td>
                  <td>{row.isMsme ? <Pill tone="orange">{row.msmeCategory}</Pill> : "—"}</td>
                  {ageing.bucketLabels.map((label) => <td key={label} className="num">{row.buckets[label] ? inr(row.buckets[label]) : "—"}</td>)}
                  <td className="num"><strong>{inr(row.total)}</strong></td>
                  <td className="num">{row.disallowable43B ? <span className="ws-red">{inr(row.disallowable43B)}</span> : "—"}</td>
                </tr>
              ))}
              {ageing.rows.length === 0 && <tr><td colSpan={ageing.bucketLabels.length + 4} className="ws-empty">Nothing outstanding.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MatchTable({ bill, documents, config }: { bill: PurchaseDocument; documents: PurchaseDocument[]; config: PurchaseConfig }) {
  const order = documents.find((document) => document.id === bill.purchaseOrderId);
  const receipts = documents.filter((document) => (bill.goodsReceiptIds ?? []).includes(document.id));
  const result = threeWayMatch(bill, order, receipts, config);
  if (!result.lines.length) return null;
  return (
    <>
      <h5>Three-way match {result.matched ? <Pill tone="green">Matched</Pill> : <Pill tone="red">Variance</Pill>}</h5>
      <table className="ws-table compact">
        <thead><tr><th>Description</th><th className="num">Ordered</th><th className="num">Received</th><th className="num">Billed</th><th className="num">Qty var</th><th className="num">Rate var</th><th>Status</th></tr></thead>
        <tbody>
          {result.lines.map((line, index) => (
            <tr key={index}>
              <td>{line.description}</td>
              <td className="num">{line.orderedQuantity}</td>
              <td className="num">{line.receivedQuantity}</td>
              <td className="num">{line.billedQuantity}</td>
              <td className="num">{line.quantityVariance ? `${line.quantityVariancePercent}%` : "—"}</td>
              <td className="num">{line.priceVariance ? `${line.priceVariancePercent}%` : "—"}</td>
              <td><Pill tone={line.status === "matched" ? "green" : "red"}>{line.status.replace("-", " ")}</Pill></td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

